import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Info, Lock, Unlock, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { sitesApi } from '@/api/client';
import { OwnerOrgSelect, ownerOrgErrorMessage, useOwnerOrgPicker } from '@/components/OwnerOrgPicker';
import { useSuperUser } from '../shared';

// ---- Create Site Dialog ----
//
// No Talos / Kubernetes selectors: a site runs the versions its hardware is
// certified at, which deployment-service derives from the certified hardware
// profiles and shows here read-only. The old selectors defaulted to Talos
// 1.12.5 / Kubernetes 1.35.0, which the launch hardware (DGX Spark) is not
// certified at, so every site created here refused its first box.
//
// Every site has an owner org (JSS#849): a superuser picks it, an org user's
// is implied (components/OwnerOrgPicker.tsx, JSS#959).

export function CreateSiteDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const isSuperUser = useSuperUser();
  const ownerOrg = useOwnerOrgPicker(open, isSuperUser);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [lockdown, setLockdown] = useState(true);
  const [controlPlaneVip, setControlPlaneVip] = useState('');
  const [controlPlaneCount, setControlPlaneCount] = useState(3);
  const [showVipTooltip, setShowVipTooltip] = useState(false);
  const [metallbIngestPool, setMetallbIngestPool] = useState('');
  const [metallbGeneralPool, setMetallbGeneralPool] = useState('');

  const platformVersions = useQuery({
    queryKey: ['site-platform-versions'],
    queryFn: () => sitesApi.platformVersions(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      sitesApi.create({
        name, description, location,
        lockdown: isSuperUser ? lockdown : undefined,
        controlPlaneVip: controlPlaneVip || undefined,
        controlPlaneCount,
        metallbIngestPool: metallbIngestPool || undefined,
        metallbGeneralPool: metallbGeneralPool || undefined,
        // An org user's org is implied: they send none.
        orgId: ownerOrg.orgId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      resetAndClose();
    },
  });

  function resetAndClose() {
    createMutation.reset();
    setName('');
    setDescription('');
    setLocation('');
    setLockdown(true);
    setControlPlaneVip('');
    setControlPlaneCount(3);
    setShowVipTooltip(false);
    onClose();
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-surface border border-surface-elevated rounded-xl w-full max-w-lg mx-4 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <h3 className="text-lg font-semibold text-silver-bright">Create Site</h3>
          <button onClick={resetAndClose} className="text-silver-muted hover:text-silver-bright">
            <X size={18} />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
          className="p-6 space-y-4"
        >
          <OwnerOrgSelect
            id="create-site-owner-org"
            picker={ownerOrg}
            noun="site"
            labelClassName="block text-xs font-medium text-silver-muted mb-1.5"
            selectClassName="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
            hintClassName="text-xs text-silver-muted mt-1"
          />
          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">Name *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
              placeholder="NYC Production"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">Location *</label>
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              required
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
              placeholder="NYC-1"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">Description</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
              placeholder="Primary broadcast facility"
            />
          </div>
          {isSuperUser && (
            <div>
              <label className="block text-xs font-medium text-silver-muted mb-1.5">Security Mode</label>
              <button
                type="button"
                onClick={() => setLockdown(!lockdown)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-sm text-left transition-colors',
                  lockdown
                    ? 'bg-status-healthy/5 border-status-healthy/30 text-status-healthy'
                    : 'bg-status-warning/5 border-status-warning/30 text-status-warning'
                )}
              >
                {lockdown ? <Lock size={14} /> : <Unlock size={14} />}
                <div>
                  <div className="font-medium">{lockdown ? 'Production' : 'Development'}</div>
                  <div className="text-xs opacity-70">
                    {lockdown
                      ? 'Managed exclusively through cloud.jeket.com'
                      : 'Local talosctl and kubectl access enabled'}
                  </div>
                </div>
              </button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <label className="block text-xs font-medium text-silver-muted">Control Plane VIP</label>
                <div className="relative">
                  <button
                    type="button"
                    onMouseEnter={() => setShowVipTooltip(true)}
                    onMouseLeave={() => setShowVipTooltip(false)}
                    className="text-silver-muted hover:text-silver-bright transition-colors"
                  >
                    <Info size={12} />
                  </button>
                  {showVipTooltip && (
                    <div className="absolute z-10 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 px-3 py-2 bg-surface-elevated border border-surface-elevated rounded-lg shadow-lg text-xs text-silver-muted">
                      Virtual IP shared between control plane nodes for high availability. Required for multi-node control planes. Enter a free IP on your site's local network. Optional for single-node setups.
                    </div>
                  )}
                </div>
              </div>
              <input
                value={controlPlaneVip}
                onChange={(e) => setControlPlaneVip(e.target.value)}
                className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright font-mono placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
                placeholder="192.168.1.100"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-silver-muted mb-1.5">CP Node Count</label>
              <input
                type="number"
                value={controlPlaneCount}
                onChange={(e) => setControlPlaneCount(Math.max(1, Math.min(5, parseInt(e.target.value) || 1)))}
                className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright font-mono placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
                min={1}
                max={5}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-silver-muted mb-1.5">MetalLB Ingest Pool</label>
              <input
                value={metallbIngestPool}
                onChange={(e) => setMetallbIngestPool(e.target.value)}
                className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright font-mono placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
                placeholder="10.0.10.100-10.0.10.149"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-silver-muted mb-1.5">MetalLB General Pool</label>
              <input
                value={metallbGeneralPool}
                onChange={(e) => setMetallbGeneralPool(e.target.value)}
                className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright font-mono placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
                placeholder="10.0.10.150-10.0.10.199"
              />
            </div>
          </div>
          <div data-testid="create-site-platform-versions" className="rounded-lg border border-surface-elevated bg-surface-deep px-3 py-2">
            <div className="text-xs font-medium text-silver-muted mb-1">Platform</div>
            {platformVersions.data ? (
              <div className="text-sm text-silver-bright">
                Talos <span className="font-mono">{platformVersions.data.talosVersion}</span>
                {' · '}
                Kubernetes <span className="font-mono">{platformVersions.data.kubernetesVersion}</span>
              </div>
            ) : (
              <div className="text-sm text-silver-muted">
                {platformVersions.isError ? 'Could not load the platform versions.' : 'Loading…'}
              </div>
            )}
            <div className="text-xs text-silver-muted mt-1">
              Set by Jeket: the versions your hardware is certified at.
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={resetAndClose}
              className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name || !location || ownerOrg.missing || createMutation.isPending}
              className="px-4 py-2 bg-gold text-surface-deep text-sm font-medium rounded-lg hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Site'}
            </button>
          </div>
          {createMutation.isError && (
            <p role="alert" className="text-xs text-status-error">
              {ownerOrgErrorMessage(createMutation.error, 'site')}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
